import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import { useRouteMatch } from 'react-router';
import {
  Grid,
  ButtonGroup,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
  ClickAwayListener,
  Tooltip,
  ButtonProps,
  ButtonGroupProps,
} from '@material-ui/core';

import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import Button from './Button';
import { useId } from '../../hooks';
import useStyles from './styles';

export type ButtonOption = {
  title: React.ReactNode;
  icon?: React.ReactNode;
  titleList?: React.ReactNode;
  tooltip?: React.ReactNode;
  onClick: (e: React.MouseEvent<HTMLLIElement | HTMLButtonElement, MouseEvent>) => void;
  disabled: boolean;
  hide?: boolean;
  withSeparator?: boolean;
  [key: string]: any;
};

type Props = ButtonProps & {
  buttons: ButtonOption[];
  selectedDefault?: () => number;
  buttonClassName?: string;
  overlay?: boolean;
  disableUpdateMainButton?: boolean;
  buttonGroupClasses?: ButtonGroupProps['classes'];
};

const ButtonGroupComponent = ({
  buttons,
  selectedDefault,
  variant = 'contained',
  color = 'primary',
  overlay = true,
  disableUpdateMainButton = true,
  buttonGroupClasses,
  ...props
}: Props) => {
  const style = useStyles();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const menuId = useId();
  const { path } = useRouteMatch<{id: string}>();
  const defaultClasses = {
    grouped: style.mainButton,
  };

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const element = listRef.current;
    if (element !== null) {
      const focusItem = (focusNext = true) => {
        const items = element.querySelectorAll('li');
        const range = items.length;
        let currentFocus = 0;

        // find current focused item
        items.forEach((item, key) => {
          if (item.tabIndex === 0) currentFocus = key;
        });
        if (!focusNext) {
          currentFocus = (currentFocus + 1 >= range) ? 0 : currentFocus + 1;
        } else {
          currentFocus = (currentFocus - 1 < 0) ? range - 1 : currentFocus - 1;
        }

        // focus selected item
        items[currentFocus].focus();

        // update index & classes
        items.forEach((item, key) => {
          // eslint-disable-next-line no-param-reassign
          item.tabIndex = (key === currentFocus) ? 0 : -1;
          if (key !== currentFocus) {
            item.classList.remove('Mui-selected');
          }
        });
      };

      switch (e.key) {
        case 'ArrowUp':
          // select up
          e.preventDefault();
          focusItem();
          break;
        case 'ArrowDown':
          // select down
          e.preventDefault();
          focusItem(false);
          break;
        case 'Tab':
          // select down
          e.preventDefault();
          focusItem(false);
          break;
        default:
          break;
      }
    }
  }, [listRef]);

  useEffect(() => {
    const keyDownListener = handleKeyDown;
    window.addEventListener('keydown', keyDownListener);
    return () => {
      window.removeEventListener('keydown', keyDownListener);
    };
  }, [handleKeyDown]);


  useEffect(() => {
    if (buttons[selectedIndex] && path !== buttons[selectedIndex].path) {
      if (selectedDefault !== undefined) {
        const index = selectedDefault();
        if (index !== -1 && index !== selectedIndex && !disableUpdateMainButton) {
          setSelectedIndex(index);
        }
      }
    }
  }, [path, buttons, selectedIndex, selectedDefault, disableUpdateMainButton]);

  const handleMenuItemClick = useCallback((
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number,
  ) => {
    if (!disableUpdateMainButton) setSelectedIndex(index);
    buttons[index].onClick(event);
    setOpen(false);
  }, [setOpen, buttons, setSelectedIndex, disableUpdateMainButton]);

  const handleToggle = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, [setOpen]);

  const handleClose = useCallback((event: React.MouseEvent<Document, MouseEvent>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  }, [setOpen]);

  if (buttons === undefined || buttons.length === 0) {
    return null;
  }

  return (
    <Grid container direction="column" alignItems="center">
      <Grid item xs={12}>
        <ButtonGroup
          variant={variant}
          color={color}
          ref={anchorRef}
          classes={buttonGroupClasses || defaultClasses}
          // classes={{
          //
          //   contained
          // }}
        >
          {
            buttons[selectedIndex].tooltip
              ? (
                <Tooltip
                  classes={{
                    tooltip: style.tooltip,
                  }}
                  title={buttons[selectedIndex].tooltip}
                  PopperProps={{ disablePortal: true }}
                >
                  <Button
                    onClick={(e) => buttons[selectedIndex].onClick(e)}
                    variant={variant}
                    color={color}
                    overlay={overlay}
                    {...props}
                  >
                    {buttons[selectedIndex].title}
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  onClick={(e) => buttons[selectedIndex].onClick(e)}
                  variant={variant}
                  color={color}
                  overlay={overlay}
                  {...props}
                >
                  {buttons[selectedIndex].title}
                </Button>
              )
          }
          <Button
            variant={variant}
            color={color}
            size="small"
            aria-controls={open ? menuId : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="menu"
            onClick={handleToggle}
            overlay={overlay}
            {...props}
          >
            <ArrowDropDownIcon />
          </Button>
        </ButtonGroup>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          style={{ zIndex: 2147483100 }}
          placement="bottom-end"
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom-end' ? 'center top' : 'center bottom',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id={menuId} ref={listRef}>
                    {buttons.map((item, index) => {
                      if (item.hide) {
                        return null;
                      }

                      return (
                        item.tooltip
                          ? (
                            <Tooltip
                              title={item.tooltip}
                              PopperProps={{ disablePortal: true }}
                              classes={{
                                tooltip: style.tooltip,
                              }}
                              key={item.title as string}
                            >
                              <MenuItem
                                key={item.title as string}
                                selected={index === selectedIndex}
                                disabled={item.disabled}
                                onClick={(event) => handleMenuItemClick(event, index)}
                              >
                                {item.title}
                              </MenuItem>
                            </Tooltip>
                          )
                          : (
                            <MenuItem
                              divider={item.withSeparator}
                              key={item.title as string}
                              selected={index === selectedIndex}
                              disabled={item.disabled}
                              onClick={(event) => handleMenuItemClick(event, index)}
                            >
                              {item.titleList
                                ? item.titleList
                                : (
                                  <span className={style.label}>
                                    <span className={style.icon}>{item.icon}</span>
                                    {item.title}
                                  </span>
                                )}
                            </MenuItem>
                          )
                      );
                    })}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Grid>
    </Grid>
  );
};

export default ButtonGroupComponent;
